import { FiArrowLeft, FiArrowRight } from 'react-icons/fi';
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { useScrollBarWidth, useThrottledResizeObserver } from 'utils/hooks';
import SemanticList from './SemanticList';
import { UnstyledButton } from 'utils/fragments';
import isFinite from 'lodash/isFinite';
import styled from 'styled-components';

const withPadding = (Component) =>
    forwardRef(
        (
            {
                padding,
                paddingTop,
                paddingRight,
                paddingBottom,
                paddingLeft,
                gap,
                ...props
            },
            ref,
        ) => {
            const receivedPaddingProps = {
                paddingTop,
                paddingRight,
                paddingBottom,
                paddingLeft,
                gap,
            };
            const paddingPropNames = Object.keys(receivedPaddingProps);
            const paddingProps = {};

            let paddingArr = [];

            if (typeof padding === 'string') {
                paddingArr = padding.split(' ');
            } else if (Array.isArray(padding)) {
                paddingArr = padding;
            } else if (isFinite(padding)) {
                paddingArr = [`${padding}px`];
            }

            if (paddingArr.length > 0) {
                paddingPropNames.forEach((name) => {
                    paddingProps[`$${name}`] = paddingArr[0];
                });
            }

            if (paddingArr.length > 1) {
                paddingProps.$paddingRight = paddingArr[1];
                paddingProps.$paddingLeft = paddingArr[1];
            }

            if (paddingArr.length > 2) {
                paddingProps.$paddingBottom = paddingArr[2];
            }

            if (paddingArr.length > 3) {
                paddingProps.$paddingLeft = paddingArr[3];
            }

            paddingProps.$gap =
                gap === undefined ? paddingProps.$paddingLeft : gap;

            Object.entries(receivedPaddingProps).forEach(([name, prop]) => {
                const isFiniteNumber = isFinite(prop);
                if (typeof prop === 'string' || isFiniteNumber) {
                    paddingProps[`$${name}`] = isFiniteNumber
                        ? `${prop}px`
                        : prop;
                }
            });

            return <Component {...paddingProps} {...props} ref={ref} />;
        },
    );

const GridFlex = styled(SemanticList)`
    display: flex;
    flex-wrap: nowrap;
    padding-top: ${({ $paddingTop }) => $paddingTop || 'initial'};
    padding-right: ${({ $paddingRight }) => $paddingRight || 'initial'};
    padding-bottom: ${({ $paddingBottom }) => $paddingBottom || 'initial'};
    padding-left: ${({ $paddingLeft }) => $paddingLeft || 'initial'};

    & > li {
        display: inline-flex;
        flex-shrink: 1;
        flex-grow: 1;
        margin-left: ${({ $gap }) => $gap || 'initial'};
        min-width: calc(100% - ${({ $gap }) => $gap || '0px'});
    }

    & > li:first-of-type {
        margin-left: 0px;
    }

    @media screen and (min-width: ${({ theme }) => theme.bp.sm}px) {
        & > li {
            min-width: auto;
        }
    }
`;

const GridFlexScroll = styled(GridFlex)`
    flex-grow: 1;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    scroll-padding: ${({ $paddingLeft }) => $paddingLeft || 'initial'};

    & > li {
        scroll-snap-align: start;
    }
`;

const ARROW_WIDTH = '26px';

const ScrollArrowButton = styled(UnstyledButton)`
    background-color: ${({ theme }) => theme.purple_5};
    color: ${({ theme }) => theme.purple_1};
    border: solid 1px ${({ theme }) => theme.purple_4};
    border-radius: 50%;
    max-width: ${ARROW_WIDTH};
    max-height: ${ARROW_WIDTH};
    width: ${ARROW_WIDTH};
    height: ${ARROW_WIDTH};
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    left: ${({ $left }) => $left};

    &:hover {
        background-color: ${({ theme }) => theme.purple_1};
        color: ${({ theme }) => theme.gray_7};
        border-color: ${({ theme }) => theme.purple_1};
    }
`;

const ButtonWrapper = styled.div`
    max-width: 0px;
    position: absolute;
    top: ${({ $paddingTop }) => $paddingTop || '0px'};
    min-width: 1px;
    z-index: 1;
    height: calc(
        100% - ${({ $paddingTop }) => $paddingTop || '0px'} -
            ${({ $paddingBottom }) => $paddingBottom || '0px'} -
            ${({ $scrollBarWidth }) => $scrollBarWidth}px
    );
    display: flex;
    align-items: center;
`;

function GridScroll({ children, ...props }) {
    const ref = useRef();
    const [scrollPosition, setScrollPosition] = useState({
        atStart: true,
        atEnd: true,
    });
    const { atStart, atEnd } = scrollPosition;
    const handleScrollOrResize = useCallback(
        (e) => {
            const element = e.target;
            if (!element) return;
            const { scrollWidth, scrollLeft, clientWidth } = element;
            const threshold = 5.0;
            setScrollPosition({
                atStart: scrollLeft < threshold,
                atEnd:
                    Math.abs(scrollWidth - scrollLeft - clientWidth) <
                    threshold,
            });
        },
        [setScrollPosition],
    );

    const { width } = useThrottledResizeObserver(250, { ref });

    const imperativeScroll = useCallback((forward = true) => {
        const element = ref.current;
        if (!element) return;
        const { scrollLeft, clientWidth } = element;
        element.scrollTo({
            top: 0,
            left: scrollLeft + (forward ? 1 : -1) * clientWidth - 18,
            behavior: 'smooth',
        });
    });

    useEffect(() => {
        if (ref && ref.current) {
            ref.current.addEventListener('scroll', handleScrollOrResize);
        }
        return () => {
            if (ref.current) {
                ref.current.removeEventListener('scroll', handleScrollOrResize);
            }
        };
    }, [handleScrollOrResize]);

    useEffect(() => {
        if (ref && ref.current) handleScrollOrResize({ target: ref.current });
    }, [handleScrollOrResize, width, children]);

    const scrollBarWidth = useScrollBarWidth();

    return (
        <div css="display: flex;">
            <div css="position: relative;">
                {!atStart && (
                    <ButtonWrapper
                        $scrollBarWidth={scrollBarWidth}
                        $paddingTop={props.$paddingTop}
                        $paddingBottom={props.$paddingBottom}
                    >
                        <ScrollArrowButton
                            $left={`calc(${props.$paddingLeft || '0px'} - ${ARROW_WIDTH} / 2)`}
                            onClick={() => imperativeScroll(false)}
                            aria-label="Scroll Left"
                        >
                            <FiArrowLeft />
                        </ScrollArrowButton>
                    </ButtonWrapper>
                )}
            </div>
            <GridFlexScroll {...props} ref={ref}>
                {children}
            </GridFlexScroll>
            <div css="position: relative;">
                {!atEnd && (
                    <ButtonWrapper
                        $scrollBarWidth={scrollBarWidth}
                        $paddingTop={props.$paddingTop}
                        $paddingBottom={props.$paddingBottom}
                    >
                        <ScrollArrowButton
                            $left={`calc(-1 * (${props.$paddingRight || '0px'} + ${ARROW_WIDTH} / 2))`}
                            onClick={() => imperativeScroll()}
                            aria-label="Scroll Right"
                        >
                            <FiArrowRight />
                        </ScrollArrowButton>
                    </ButtonWrapper>
                )}
            </div>
        </div>
    );
}

const PaddedGridScrollWithProps = withPadding(GridScroll);

function PaddedGridScroll({ padding = 20, ...props }) {
    return <PaddedGridScrollWithProps padding={padding} {...props} />;
}

export default PaddedGridScroll;
